import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import Layout from '../../components/Layout';
import { fleetService } from '../../services/firestore';
import useFleetUsage from '../../hooks/useFleetUsage';
import { fleetServiceHistoryService } from '../../services/fleetServiceHistoryService';
import RequirePermission from '../../components/RequirePermission';
import EjarAccountCard from '../../components/EjarAccountCard';
import { PERMISSIONS } from '../../constants/permissions';

import logger from '../../utils/logger';
import { useSearchLogger } from '../../hooks/useSearchLogger';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { adminService } from '../../services/adminService';

import LicenseModal from '../../components/LicenseModal';
import SmartNavView from '../../components/SmartNavView';
import SummaryView from '../../components/SummaryView';
import ExpiryModal from '../../components/ExpiryModal';
import Button from '../../ui/Button';
import IconButton from '../../ui/IconButton';
import Card from '../../ui/Card';
import Badge from '../../ui/Badge';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { getStatusLabel } from '../../ui/Status/status-map';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { CellText, CellPlate, CellNumber, CellMoney, CellMuted, CellStatus, CellTag, CellDate, toneClass } from '../../ui/DataTable/cells';
import Stat, { StatGrid } from '../../ui/Stat/Stat';
import EmptyState from '../../ui/EmptyState';
import Dialog, { Drawer } from '../../ui/Dialog/Dialog';
import { Field, Input, Select } from '../../ui/Field/Field';
import { toast, confirmDialog, promptDialog } from '../../ui/feedback';
import LoadingSpinner from '../../components/LoadingSpinner';

import { useSearchParams, useNavigate } from 'react-router-dom';
import { normalizePlate as sharedNormalizePlate, parseDateTime } from '../../services/possessionMatcher';
import Popover from '../../ui/Popover';
import CustomerProfileModal from '../../components/CustomerProfileModal';
import { CellId } from '../../ui/DataTable/cells';
import { openContractFor, loadOpenContracts } from '../../services/openContracts';
import { contractDateText } from '../DailyBookingReport/AvailabilityDialog';
import { finesService, errorMessage } from '../../services/finesService';
import { followJob } from '../../services/followJob';
import { statusMapOf } from '../../services/fleetStatusMap';
import { ensureSaved } from '../../services/writeResult';
import { highlightParts, companyOf, hasCompany, makeOilStatus, fleetExportRows } from './fleetTable.logic';
import { inFleet, isCompanyCar, isExpired, isExpiringSoon, daysToExpiry } from '../../services/fleetStatus';
import { dubaiDateTime } from '../../utils/dubaiTime';

export default function FleetManager() {
    const { currentUser } = useAuth();
    // each button its own permission (permissions phase 3, 4 Oct 2026), through usePermissions ("show the app as this
    // role" shows the role's buttons): many cars at once, a car's edit (its save is a fleet write), the licence
    const { hasPermission, hasAllPermissions } = usePermissions();
    const canBulk = hasPermission('fleet_bulk_remarks');
    const canEditCar = hasAllPermissions(['fleet_edit_vehicle', PERMISSIONS.write_fleet]);
    const canLicense = hasPermission('fleet_download_license');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Core State
    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [licenseUrl, setLicenseUrl] = useState(null);
    const [statusMap, setStatusMap] = useState({});
    const [serviceRecords, setServiceRecords] = useState([]);

    const [selectedPlates, setSelectedPlates] = useState([]);

    // Full Edit Vehicle State
    const [editVehicleModalOpen, setEditVehicleModalOpen] = useState(false);
    const [editingCar, setEditingCar] = useState(null);

    // Notifications go through the shared toast
    const showNotification = (message, type = 'success') => {
        if (type === 'success') toast.success(message);
        else toast.error(message);
    };

    // Advanced Features State
    const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

    // Sync URL params to Search Query (for AI deep linking)
    useEffect(() => {
        const query = searchParams.get('search');
        if (query) {
            setSearchQuery(query);
            setView('table');
        }
    }, [searchParams]);

    const logFleetSearch = useSearchLogger('FLEET');
    // Pre-select the "Expired" tile when deep-linked with ?expiry=expired (read once, on mount).
    const [activeTab, setActiveTab] = useState(() => (searchParams.get('expiry') === 'expired' ? 'expired' : 'all'));
    const [view, setView] = useState('table'); // 'table' | 'smart' | 'summary'

    // Smart Nav State
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedMake, setSelectedMake] = useState(null);

    // Expiry Modal State
    const [expiryModalOpen, setExpiryModalOpen] = useState(false);
    const [expiryDateInput, setExpiryDateInput] = useState('');
    const [expiryFilterDate, setExpiryFilterDate] = useState(null);
    
    // Multi-select Filter State
    const [selectedFilters, setSelectedFilters] = useState({
        categories: [],
        makes: [],
        statuses: [],
        years: [],
        regDateStart: '',
        regDateEnd: '',
        insDateStart: '',
        insDateEnd: '',
        kmMin: '',
        kmMax: '',
        colors: [],
        models: []
    });
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [savedFilters, setSavedFilters] = useState(() => {
        const saved = localStorage.getItem('YELO_FLEET_FILTERS');
        return saved ? JSON.parse(saved) : [];
    });

    useEffect(() => {
        localStorage.setItem('YELO_FLEET_FILTERS', JSON.stringify(savedFilters));
    }, [savedFilters]);

    const saveCurrentFilter = (name) => {
        if (!name) return;
        const newPreset = { id: Date.now(), name, filters: { ...selectedFilters } };
        setSavedFilters(prev => [...prev, newPreset]);
    };

    const deleteSavedFilter = (id) => {
        setSavedFilters(prev => prev.filter(f => f.id !== id));
    };

    const loadSavedFilter = (filters) => {
        setSelectedFilters({ models: [], ...filters });
    };

    const toggleFilter = (type, value, isDirect = false) => {
        setSelectedFilters(prev => {
            if (isDirect) return { ...prev, [type]: value };
            const current = prev[type];
            const updated = current.includes(value)
                ? current.filter(v => v !== value)
                : [...current, value];
            return { ...prev, [type]: updated };
        });
    };

    const clearFilters = () => {
        setSelectedFilters({
            categories: [],
            makes: [],
            statuses: [],
            years: [],
            regDateStart: '',
            regDateEnd: '',
            insDateStart: '',
            insDateEnd: '',
            kmMin: '',
            kmMax: '',
            colors: [],
            models: []
        });
    };

    // Helper Functions (Moved up to prevent initialization errors)
    // Page-level wrapper: this page keys statusMap/lookups by lower-case-no-spaces plates.
    const normalizePlate = (plate) => sharedNormalizePlate(plate).toLowerCase();

    // Stable per status map, so the oil statuses below are not rebuilt on every keystroke
    const getCarKm = useCallback((plate) => statusMap[normalizePlate(plate)]?.km || '0',
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [statusMap]);

    const getCarStatus = (plate) => {
        const cleanPlate = normalizePlate(plate);
        const statusData = statusMap[cleanPlate];
        return statusData?.status || '';
    };

    const getCarBranch = (plate) => statusMap[normalizePlate(plate)]?.branch || '';

    // a car moved by the fast transfer shows its new branch at once (the engine saved it too)
    const markMoved = (plate, result) => {
        if (!result || !result.branch) return;
        setStatusMap((prev) => {
            const entry = { ...(prev[normalizePlate(plate)] || {}), branch: result.branch };
            return { ...prev, [plate]: entry, [normalizePlate(plate)]: entry };
        });
    };

    // One pass over the service records per data change (not cars x records per render)
    const getOilStatus = useMemo(() => makeOilStatus({ cars, serviceRecords, kmOf: getCarKm }),
        [cars, serviceRecords, getCarKm]);

    // The fleet's one rule (services/fleetStatus): on the RTA list and not sold / stolen / Ready To Sell.
    // Every card, the tab it opens and the Smart / Summary views count with it, so a card and its tab
    // always hold the same cars (audit FLT-01, FLT-02).
    const isInFleet = (c) => inFleet(c, getCarStatus(c.plate_number));

    // Summary Calculation
    const summary = useMemo(() => {
        const now = new Date();
        const fleet = cars.filter(isInFleet);
        const yelo = fleet.filter((c) => !isCompanyCar(c));

        return {
            total: fleet.length,
            active: yelo.length,                                            // Yelo cars
            available: yelo.filter((c) => AVAILABLE_STATUSES.includes(getCarStatus(c.plate_number).toLowerCase())).length,
            // Dubai calendar days: a registration valid through today is not expired yet (FLT-22)
            expired: fleet.filter((c) => isExpired(c.registration_expiry, now)).length,
            expiringSoon: fleet.filter((c) => isExpiringSoon(c.registration_expiry, now)).length,
            maintenance: fleet.filter((c) => String(c.status).toLowerCase().includes('maintenance')).length,
            invygo: fleet.filter(isCompanyCar).length,                      // Other Companies
            // everything else: off the RTA list, or sold / stolen / Ready To Sell (still in RTA)
            sold: cars.length - fleet.length,
            // a sold car is nobody's oil change any more
            oilDue: fleet.filter((c) => getOilStatus(c.plate_number).label === 'DUE').length
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cars, serviceRecords, statusMap, getOilStatus]);

    // What each car is doing now (Monthly / Daily / Invygo / Leasing / Airport): shared rule, see hooks/useFleetUsage
    const { usageOf, ready: rentalsReady } = useFleetUsage(cars);

    // Cars in the fleet (the one rule above). The Smart and Summary views show only these, the same
    // cars as Total Fleet: sold / Ready To Sell cars stay reachable from the List view's "Sold" card.
    const fleetCars = useMemo(() => cars.filter(isInFleet),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cars, statusMap]);

    // Enhanced Filtering Logic: `baseCars` = tab, dates, KM and search; the multi-select choices
    // (facets) come on top of it, so each facet can offer only what the OTHER choices leave
    const baseCars = useMemo(() => {
        let result = cars;

        // 1. Expiry Filter (Overrides others if active) - cars still in the fleet only
        if (expiryFilterDate) {
            return result.filter(c => {
                if (!isInFleet(c)) return false;
                const d = parseDateTime(c.registration_expiry);
                return d && d <= expiryFilterDate;
            });
        }

        const now = new Date();

        // 2. Tab filtering: each tab holds exactly the cars its card counts (FLT-01, FLT-02)
        switch (activeTab) {
            case 'all':
                result = result.filter(isInFleet);
                break;
            case 'active':
                result = result.filter(c => isInFleet(c) && !isCompanyCar(c));
                break;
            case 'maintenance':
                result = result.filter(c => isInFleet(c) && String(c.status).toLowerCase().includes('maintenance'));
                break;
            case 'invygo':
                result = result.filter(c => isInFleet(c) && isCompanyCar(c));
                break;
            case 'expired':
                result = result.filter(c => isInFleet(c) && isExpired(c.registration_expiry, now));
                break;
            case 'sold':
                result = result.filter(c => !isInFleet(c));
                break;
            default:
                break;
        }

        // 4. Date Range Filtering (Reg & Ins)
        const filterByDateRange = (data, field, start, end) => {
            if (!start && !end) return data;
            return data.filter(c => {
                const date = parseDateTime(c[field]);
                if (!date) return false;
                if (start && date < new Date(start)) return false;
                if (end && date > new Date(end)) return false;
                return true;
            });
        };

        result = filterByDateRange(result, 'registration_expiry', selectedFilters.regDateStart, selectedFilters.regDateEnd);
        result = filterByDateRange(result, 'insurance_expiry', selectedFilters.insDateStart, selectedFilters.insDateEnd);

        // 5. KM Range Filtering
        if (selectedFilters.kmMin || selectedFilters.kmMax) {
            result = result.filter(c => {
                const km = Number(getCarKm(c.plate_number)) || 0;
                if (selectedFilters.kmMin && km < Number(selectedFilters.kmMin)) return false;
                if (selectedFilters.kmMax && km > Number(selectedFilters.kmMax)) return false;
                return true;
            });
        }

        // 6. Search query
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(c =>
                String(c.plate_number).toLowerCase().includes(q) ||
                String(c.make).toLowerCase().includes(q) ||
                String(c.model).toLowerCase().includes(q) ||
                String(c.vin).toLowerCase().includes(q) ||
                (c.previous_plates || []).some(p => String(p).toLowerCase().includes(q)) ||
                String(c.remarks).toLowerCase().includes(q)
            );
        }

        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cars, activeTab, searchQuery, expiryFilterDate, selectedFilters.regDateStart, selectedFilters.regDateEnd,
        selectedFilters.insDateStart, selectedFilters.insDateEnd, selectedFilters.kmMin, selectedFilters.kmMax, statusMap]);

    // A car's value for one facet (the status comes from the live status map)
    const facetValue = (c, key) => {
        switch (key) {
            case 'statuses': return getCarStatus(c.plate_number).toLowerCase();
            case 'categories': return c.category;
            case 'makes': return foldText(c.make);        // "Peugeot" / "PEUGEOT", "Citroën" / "CITROEN": one choice
            case 'models': return foldText(c.model);
            case 'years': return c.year;
            case 'colors': return foldText(c.color).replace(/^GREY$/, 'GRAY');   // one grey
            default: return '';
        }
    };
    const applyFacets = (list, except) => {
        const out = FACET_KEYS.reduce((acc, key) => {
            const chosen = selectedFilters[key] || [];
            if (key === except || !chosen.length) return acc;
            return acc.filter((c) => chosen.includes(String(facetValue(c, key) ?? '').trim()));
        }, list);
        // Sold / stolen / Ready To Sell cars are already out of every tab but Sold (the tabs use the
        // fleet's one rule), so the facets need no rule of their own
        return out;
    };

    const filteredCars = useMemo(() => (expiryFilterDate ? baseCars : applyFacets(baseCars, null)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [baseCars, expiryFilterDate, selectedFilters, statusMap, activeTab]);

    // Each facet lists what the OTHER choices leave, with how many cars (a chosen value stays shown)
    const filterOptions = useMemo(() => {
        const opts = { totalCount: baseCars.length };
        FACET_KEYS.forEach((key) => {
            const counts = new Map();
            applyFacets(baseCars, key).forEach((c) => {
                const v = String(facetValue(c, key) ?? '').trim();
                if (v) counts.set(v, (counts.get(v) || 0) + 1);
            });
            (selectedFilters[key] || []).forEach((v) => { if (!counts.has(v)) counts.set(v, 0); });
            const list = [...counts.entries()].map(([value, count]) => ({ value, count }));
            if (key === 'years') list.sort((a, b) => b.value.localeCompare(a.value));
            else if (key === 'statuses' || key === 'colors') list.sort((a, b) => (b.count - a.count) || a.value.localeCompare(b.value));
            else list.sort((a, b) => a.value.localeCompare(b.value));
            opts[key] = list;
        });
        return opts;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseCars, selectedFilters, statusMap]);

    useEffect(() => {
        loadFleetFromDB(); // Default: Use Cache (it builds the status map too)
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [lastSyncTime, setLastSyncTime] = useState(null);

    // Refresh reads the cars from Ejar first, then the page (owner, 5 Oct 2026: a car moved on Ejar after the hourly
    // pull stayed at its old branch whatever Refresh did); Ejar not answering: the last hourly read, said so
    const [pullingEjar, setPullingEjar] = useState(false);
    const refreshFromEjar = async () => {
        setPullingEjar(true);
        try {
            await finesService.storeVehiclesPull();
        } catch (e) {
            toast.warning(`The cars were not read from Ejar now (${errorMessage(e)}) - showing the last hourly read`);
        } finally {
            setPullingEjar(false);
        }
        await loadFleetFromDB(true);
    };

    const loadFleetFromDB = async (force = false) => {
        setLoading(true);
        try {
            // 1. Fleet & Service Records. The status map is built from the same cars: it used to
            // be a second (and, on open, a third) read of the fleet (audit FLT-21).
            // the service records apart: their failure only hides the oil column, never the whole fleet (review 18 Oct 2026)
            const [data, records] = await Promise.all([
                fleetService.getAllCars(force),
                fleetServiceHistoryService.getServiceRecords().catch((e) => {
                    logger.error('Service records not read', e);
                    toast.warning(`The service records could not be read (${errorMessage(e)}) - the oil status may be wrong`);
                    return [];
                }),
            ]);

            setServiceRecords(records);

            const { map } = statusMapOf(data);
            setStatusMap(map);

            setLastSyncTime(dubaiDateTime(new Date()));    // when this page read the fleet (Dubai)

            if (data && data.length > 0) {
                const formatted = data
                    .map(car => {
                        const plate = normalizePlate(car.plate_number);
                        const statusData = map[plate];
                        return {
                            ...car,
                            plate_number: car.plate_number || '',
                            make: car.make || '',
                            model: car.model || '',
                            year: car.year || '',
                            status: car.status || 'active',
                            current_km: statusData?.km || car.current_km || 0
                        };
                    });
                setCars(formatted);
                if (force) showNotification('Data refreshed from Server', 'success');
            }
            // IMPORTANT: If DB is empty, we do NOT overwrite if we already have cars (e.g. from upload)
            // But initial load is fine. 
        } catch (error) {
            logger.error("Failed to load fleet", error);
            toast.error(`The fleet could not be read: ${errorMessage(error)}`);   // never a silent empty fleet
        } finally {
            setLoading(false);
        }
    };

    const handleExpirySearch = () => {
        if (!expiryDateInput) return;
        setExpiryFilterDate(new Date(expiryDateInput));
        setExpiryModalOpen(false);
        setView('table');
    };

    const clearExpiryFilter = () => {
        setExpiryFilterDate(null);
        setExpiryDateInput('');
    };


    const scrollToTop = () => {
        const scrollContainer = document.getElementById('main-content-scroll');
        if (scrollContainer) {
            scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleExport = () => {
        // The same columns and values as the table on screen (see fleetExportRows)
        const dataToExport = fleetExportRows(filteredCars, {
            kmOf: getCarKm, statusOf: getCarStatus, branchOf: getCarBranch, oilOf: getOilStatus, usageOf,
        });

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Fleet_Report");
        XLSX.writeFile(wb, "Yelo_Fleet_Report.xlsx");
    };

    const handleBulkClearRemarks = async () => {
        if (!selectedPlates.length) return;
        if (!(await confirmDialog({
            title: 'Clear remarks?',
            message: `Are you sure you want to clear remarks for ${selectedPlates.length} cars (Returns to Yelo)?`,
            confirmLabel: 'Clear to Yelo',
        }))) return;

        setSyncing(true);
        try {
            const updates = selectedPlates.map(plate => ({ plate_number: plate, remarks: '' }));
            // a refused write must not be reported as done (audit FLT-04); a car changed by someone else meanwhile
            // (the store) is not written - the list is read again so the others show as saved
            const res = await fleetService.executeMasterSync(updates);
            if (res?.code === 'record-changed') loadFleetFromDB(true);
            ensureSaved(res, 'Clear to Yelo');
            showNotification(`${selectedPlates.length} cars remarks cleared successfully!`, 'success');
            setSelectedPlates([]);
            loadFleetFromDB(true);
        } catch (error) {
            showNotification(`Failed to clear cars in bulk: ${error.message}`, 'error');
        } finally {
            setSyncing(false);
        }
    };

    const handleBulkSetRemarks = async () => {
        if (!selectedPlates.length) return;
        const newRemarks = await promptDialog({
            title: 'Set company / remark',
            label: `Enter the new company or remark for ${selectedPlates.length} cars (e.g. INVYGO, Shift, Avis):`,
            placeholder: 'INVYGO',
        });
        if (newRemarks === null) return; // User cancelled

        setSyncing(true);
        try {
            const updates = selectedPlates.map(plate => ({ plate_number: plate, remarks: newRemarks }));
            const res = await fleetService.executeMasterSync(updates);
            if (res?.code === 'record-changed') loadFleetFromDB(true);
            ensureSaved(res, 'Set company / remark');
            showNotification(`${selectedPlates.length} cars updated successfully!`, 'success');
            setSelectedPlates([]);
            loadFleetFromDB(true);
        } catch (error) {
            showNotification(`Failed to update cars in bulk: ${error.message}`, 'error');
        } finally {
            setSyncing(false);
        }
    };

    const handleEditVehicleClick = (car, e) => {
        e.stopPropagation();
        setEditingCar(car);
        setEditVehicleModalOpen(true);
    };

    const handleEditVehicleSave = async (plateNumber, updatedData) => {
        try {
            setSyncing(true);
            // the row is patched and "updated" shown only when the write was taken (audit FLT-04). The store: saved on
            // the version the car was opened with - changed by someone else since, nothing is saved and the list is
            // read again; saved, the row takes the car's new version (the next edit names it)
            const res = await fleetService.updateCar(plateNumber, updatedData, { version: editingCar?.version });
            if (res?.code === 'record-changed') loadFleetFromDB(true);
            ensureSaved(res, 'Vehicle update');
            const version = res?.car?.version;

            setCars(prevCars => {
                const index = prevCars.findIndex(c => String(c.plate_number) === String(plateNumber));
                if (index === -1) return prevCars;
                const newCars = [...prevCars];
                newCars[index] = { ...newCars[index], ...updatedData, ...(version !== undefined ? { version } : {}) };
                return newCars;
            });

            showNotification('Vehicle updated successfully!', 'success');
            setEditVehicleModalOpen(false);
        } catch (error) {
            showNotification(`Failed to update vehicle: ${error.message}`, 'error');
            logger.error('Update vehicle error:', error);
        } finally {
            setSyncing(false);
        }
    };

    const activeFilterCount = Object.values(selectedFilters).reduce((acc, curr) => acc + (Array.isArray(curr) ? curr.length : (curr !== '' ? 1 : 0)), 0);

    const OIL_TONES = { red: 'danger', yellow: 'warning', green: 'success', gray: 'neutral' };
    // Car status colours as before the unify pass: rented blue, available/ready green, maintenance orange.
    const CAR_STATUS_TONES = { rented: 'info', available: 'success', ready: 'success', maintenance: 'warning' };

    return (
        <Layout
            title="Fleet Manager"
            subtitle={lastSyncTime ? `Read ${lastSyncTime}` : ''}
            actions={(
                <>
                    <RequirePermission permission={PERMISSIONS.export_fleet}>
                        <Button
                            onClick={() => {
                                handleExport();
                                adminService.logUserAction('EXPORT_FLEET', 'Exported Fleet Data', currentUser);
                            }}
                            size="sm"
                            leadingIcon={<Icon glyph={ICONS.export} size="sm" />}>
                            Export
                        </Button>
                    </RequirePermission>

                    {/* Force Refresh Button for Cache */}
                    <Button
                        onClick={refreshFromEjar}
                        size="sm"
                        disabled={pullingEjar || loading}
                        title={`Read the cars from Ejar now, then the page${lastSyncTime ? ` (last updated: ${lastSyncTime})` : ''}`}
                        leadingIcon={<Icon glyph={ICONS.refresh} size="sm" className={pullingEjar || loading ? 'animate-spin' : ''} />}
                    >
                        {pullingEjar ? 'Reading Ejar…' : loading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                </>
            )}
        >
            {/* Views, expiry filter, search and filters */}
            <div className="ds-toolbar mb-4">
                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" role="group" aria-label="View">
                    <Button
                        onClick={() => { setView('table'); clearExpiryFilter(); }}
                        variant={view === 'table' ? 'secondary' : 'ghost'}
                        size="sm"
                        aria-pressed={view === 'table'}
                        leadingIcon={<Icon glyph={ICONS.list} size="sm" />}>
                        List
                    </Button>
                    <RequirePermission permission={PERMISSIONS.view_fleet_smart}>
                        <Button
                            onClick={() => { setView('smart'); clearExpiryFilter(); }}
                            variant={view === 'smart' ? 'secondary' : 'ghost'}
                            size="sm"
                            aria-pressed={view === 'smart'}
                            leadingIcon={<Icon glyph={ICONS.matcher} size="sm" />}>
                            Smart
                        </Button>
                    </RequirePermission>
                    <RequirePermission permission={PERMISSIONS.view_fleet_summary}>
                        <Button
                            onClick={() => { setView('summary'); clearExpiryFilter(); }}
                            variant={view === 'summary' ? 'secondary' : 'ghost'}
                            size="sm"
                            aria-pressed={view === 'summary'}
                            leadingIcon={<Icon glyph={ICONS.reports} size="sm" />}>
                            Summary
                        </Button>
                    </RequirePermission>
                </div>

                <RequirePermission permission={PERMISSIONS.view_fleet_expiry}>
                    <Button
                        onClick={() => setExpiryModalOpen(true)}
                        size="sm"
                        aria-pressed={Boolean(expiryFilterDate)}
                        className={expiryFilterDate ? 'border-purple-500 text-purple-700' : ''}
                        leadingIcon={<Icon glyph={ICONS.calendar} size="sm" />}
                        title="Filter by Expiry Date">
                        {expiryFilterDate ? expiryFilterDate.toLocaleDateString('en-GB') : 'Expiry'}
                    </Button>
                </RequirePermission>

                {/* Search (big, in the middle) + Filters (at the end) */}
                <RequirePermission permission={PERMISSIONS.search_fleet}>
                    <div className="flex items-center gap-3 w-full md:w-auto md:flex-1 min-w-0">
                        <div className="relative flex-1 min-w-0 w-full max-w-[640px] mx-auto">
                            <div className="ds-search ds-search--lg" style={{ flex: 'none', width: '100%' }}>
                                <Icon glyph={ICONS.search} size="sm" />
                                <input
                                    type="search"
                                    aria-label="Search fleet"
                                    placeholder="Search by plate, make, model, VIN…"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        logFleetSearch(e.target.value);
                                    }}
                                    style={searchQuery ? { paddingInlineEnd: 32 } : undefined}
                                />
                            </div>
                            {searchQuery && (
                                <IconButton
                                    icon={ICONS.close}
                                    label="Clear search"
                                    size="xs"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-1 top-1/2 -translate-y-1/2"
                                />
                            )}
                        </div>
                        <Button
                            onClick={() => setSidebarOpen(true)}
                            size="sm"
                            className="shrink-0"
                            title="Advanced Filters"
                            leadingIcon={<Icon glyph={ICONS.filter} size="sm" />}
                        >
                            Filters
                            {activeFilterCount > 0 && (
                                <Badge variant="accent" size="sm" className="ml-1.5">{activeFilterCount}</Badge>
                            )}
                        </Button>
                    </div>
                </RequirePermission>
            </div>

            {/* moving a car on Ejar is made under the user's own Ejar login: who has not saved it yet is asked here
                (owner, 5 Oct 2026: the airport's transfers were refused and nothing said why) */}
            <RequirePermission permission="fleet_ejar_transfer" fallback={null}>
                <div className="mb-4"><EjarAccountCard onlyWhenMissing /></div>
            </RequirePermission>

            {loading ? (
                <LoadingSpinner message="Loading fleet…" />
            ) : (
                <>
                    {view === 'table' && (
                        <div>
                            {expiryFilterDate ? (
                                <div className="border p-4 mb-4 flex flex-wrap justify-between items-center gap-3 bg-amber-50 rounded-lg border-amber-200 text-sm">
                                    <div className="text-amber-900">
                                        <strong className="font-semibold">Filtering by Expiry Date:</strong> Before {expiryFilterDate.toLocaleDateString('en-GB')}
                                        <br />
                                        Found <span className="font-semibold">{filteredCars.length}</span> cars expiring on or before this date.
                                    </div>
                                    <Button onClick={clearExpiryFilter} size="sm">
                                        Clear Filter
                                    </Button>
                                </div>
                            ) : (
                                <RequirePermission permission={PERMISSIONS.view_fleet_stats}>
                                    <StatGrid min={160} className="mb-4">
                                        <SummaryCard label="Total Fleet" value={summary.total} icon={ICONS.fleet} active={activeTab === 'all'} onClick={() => setActiveTab('all')} />
                                        <RequirePermission permission={PERMISSIONS.view_fleet_stat_active} fallback={null}>
                                            <SummaryCard label="Yelo Cars" value={summary.active} icon={ICONS.car} active={activeTab === 'active'} onClick={() => setActiveTab('active')} />
                                        </RequirePermission>
                                        <SummaryCard label="Available now" value={summary.available} tone={summary.available > 0 ? 'success' : undefined} icon={ICONS.check}
                                            meta="Ready to rent (Yelo cars)"
                                            active={activeTab === 'active' && (selectedFilters.statuses || []).length > 0 && selectedFilters.statuses.every((st) => AVAILABLE_STATUSES.includes(st))}
                                            onClick={() => {
                                                setActiveTab('active');
                                                const present = AVAILABLE_STATUSES.filter((st) => cars.some((c) => getCarStatus(c.plate_number).toLowerCase() === st));
                                                setSelectedFilters((prev) => ({ ...prev, statuses: present.length ? present : ['ready'] }));
                                            }} />
                                        <RequirePermission permission={PERMISSIONS.view_fleet_stat_expired} fallback={null}>
                                            <SummaryCard label="Expired" value={summary.expired} tone={summary.expired > 0 ? 'danger' : undefined} icon={ICONS.expired} active={activeTab === 'expired'} onClick={() => setActiveTab('expired')} />
                                        </RequirePermission>
                                        <RequirePermission permission={PERMISSIONS.view_fleet_stat_invygo} fallback={null}>
                                            <SummaryCard label="Other Companies" value={summary.invygo} icon={ICONS.booking} active={activeTab === 'invygo'} onClick={() => setActiveTab('invygo')} />
                                        </RequirePermission>

                                        <SummaryCard label="Sold" value={summary.sold} icon={ICONS.archive} active={activeTab === 'sold'} onClick={() => setActiveTab('sold')} />

                                        <RequirePermission permission={PERMISSIONS.view_service_history}>
                                            <SummaryCard
                                                label="Oil Due"
                                                value={summary.oilDue}
                                                tone={summary.oilDue > 0 ? 'danger' : undefined}
                                                icon={ICONS.maintenance}
                                                meta="Open service history"
                                                onClick={() => navigate('/fleet-service-history?tab=upcoming')}
                                            />
                                        </RequirePermission>
                                    </StatGrid>
                                </RequirePermission>
                            )}

                            <FilterChips
                                selected={selectedFilters}
                                onToggle={toggleFilter}
                                onClear={clearFilters}
                            />

                            {canBulk && selectedPlates.length > 0 && (
                                <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg mb-4 flex justify-between items-center flex-wrap gap-3" role="region" aria-label="Bulk actions">
                                    <div className="text-sm font-semibold text-gray-900">
                                        {selectedPlates.length} Cars Selected
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button size="sm" onClick={handleBulkSetRemarks} isLoading={syncing}>
                                            Set Custom Status / Company
                                        </Button>
                                        <Button size="sm" onClick={handleBulkClearRemarks} disabled={syncing}>
                                            Clear to Yelo
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => setSelectedPlates([])}>
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {filteredCars.length === 0 ? (
                                <Card>
                                    <EmptyState
                                        icon={ICONS.car}
                                        title="No cars found"
                                        description={searchQuery || activeFilterCount > 0 || expiryFilterDate
                                            ? 'No cars match the current search or filters.'
                                            : 'There are no cars in this group.'}
                                    />
                                </Card>
                            ) : (
                                <TableContainer maxHeight="70vh" className="overflow-auto">
                                    <Table sticky>
                                        <TableHead>
                                            <TableRow>
                                                {canBulk && <TableHeader className="w-10">
                                                    <input
                                                        type="checkbox"
                                                        aria-label="Select all cars"
                                                        checked={filteredCars.length > 0 && selectedPlates.length === filteredCars.length}
                                                        onChange={(e) => {
                                                            if (e.target.checked) setSelectedPlates(filteredCars.map(c => String(c.plate_number)));
                                                            else setSelectedPlates([]);
                                                        }}
                                                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                                                    />
                                                </TableHeader>}
                                                <TableHeader className="w-10">#</TableHeader>
                                                <TableHeader className="w-12"><span className="sr-only">Edit</span></TableHeader>
                                                <TableHeader>Class</TableHeader>
                                                <TableHeader>Manufacturer</TableHeader>
                                                <TableHeader>Model</TableHeader>
                                                <TableHeader>Year</TableHeader>
                                                <TableHeader>Plate No</TableHeader>
                                                <TableHeader>Chassis No</TableHeader>
                                                <TableHeader>KM</TableHeader>
                                                <TableHeader>License</TableHeader>
                                                <TableHeader>Status</TableHeader>
                                                <TableHeader>Car Status</TableHeader>
                                                <TableHeader>Reg Exp</TableHeader>
                                                <TableHeader>Oil Status</TableHeader>
                                                <TableHeader>Color</TableHeader>
                                                <TableHeader>Rental Rate</TableHeader>
                                                <TableHeader>Remarks</TableHeader>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredCars.map((row, i) => (
                                                <TableRow key={i}>
                                                    {canBulk && <TableCell>
                                                        <input
                                                            type="checkbox"
                                                            aria-label={`Select ${row.plate_number}`}
                                                            checked={selectedPlates.includes(String(row.plate_number))}
                                                            onChange={(e) => {
                                                                if (e.target.checked) setSelectedPlates([...selectedPlates, String(row.plate_number)]);
                                                                else setSelectedPlates(selectedPlates.filter(p => p !== String(row.plate_number)));
                                                            }}
                                                            className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                                                        />
                                                    </TableCell>}
                                                    <TableCell>
                                                        {i + 1}
                                                    </TableCell>
                                                    <TableCell>
                                                        {canEditCar && (
                                                            <IconButton
                                                                icon={ICONS.edit}
                                                                label={`Edit ${row.plate_number}`}
                                                                size="xs"
                                                                onClick={(e) => handleEditVehicleClick(row, e)}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell><CellText title={row.category}><HighlightText text={row.category} highlight={searchQuery} /></CellText></TableCell>
                                                    <TableCell><CellText title={row.make}><HighlightText text={row.make} highlight={searchQuery} /></CellText></TableCell>
                                                    <TableCell><CellText title={row.model}><HighlightText text={row.model} highlight={searchQuery} /></CellText></TableCell>
                                                    <TableCell><span className="ds-cell-num">{row.year}</span></TableCell>
                                                    <TableCell>
                                                        <CellPlate><HighlightText text={row.plate_number} highlight={searchQuery} /></CellPlate>
                                                        {row.previous_plates?.length > 0 && (
                                                            <span title="Previous plate numbers of this car">
                                                                <CellMuted>was {row.previous_plates.join(', ')}</CellMuted>
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell><CellText title={row.vin}><HighlightText text={row.vin} highlight={searchQuery} /></CellText></TableCell>
                                                    <TableCell>
                                                        <CellNumber value={Number(getCarKm(row.plate_number))} />
                                                    </TableCell>
                                                    <TableCell>
                                                        {(() => {
                                                            const plate = String(row.plate_number || '');
                                                            const letters = plate.replace(/[0-9]/g, '').trim();
                                                            const numbers = plate.replace(/[^0-9]/g, '').trim();
                                                            const query = `${letters}_${numbers}`;
                                                            if (!canLicense) return <CellMuted>—</CellMuted>;
                                                            return (
                                                                <Button
                                                                    variant="link"
                                                                    size="xs"
                                                                    aria-label={`View license PDF for ${row.plate_number}`}
                                                                    className={toneClass('danger')}
                                                                    leadingIcon={<Icon glyph={ICONS.document} size="xs" tone="danger" />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setLicenseUrl(query); // Pass the Plate ID (e.g. AA_14284) to the modal
                                                                        adminService.logUserAction('VIEW_LICENSE_PDF', `Viewed License PDF for ${row.plate_number}`, currentUser, { plate: row.plate_number });
                                                                    }}
                                                                >
                                                                    PDF
                                                                </Button>
                                                            );
                                                        })()}
                                                    </TableCell>
                                                    <TableCell>
                                                        {(() => {
                                                            const val = companyOf(row);
                                                            const carStatusLower = String(row.status || '').toLowerCase();
                                                            let companyTone = 'neutral';
                                                            if (hasCompany(row)) companyTone = 'accent';
                                                            else if (carStatusLower === 'active') companyTone = 'success';
                                                            else if (carStatusLower === 'maintenance') companyTone = 'warning';

                                                            return <CellTag tone={companyTone}>{val}</CellTag>;
                                                        })()}
                                                    </TableCell>
                                                    <TableCell>
                                                        {(() => {
                                                            const carStatus = getCarStatus(row.plate_number);
                                                            if (!carStatus) return <CellStatus />;
                                                            const statusLower = carStatus.toLowerCase();
                                                            const cell = (
                                                                <CellStatus
                                                                    status={carStatus}
                                                                    label={getStatusLabel(carStatus)}
                                                                    title={carStatus}
                                                                    tone={CAR_STATUS_TONES[statusLower] || 'neutral'}
                                                                />
                                                            );
                                                            // every car not on rent opens the transfer: Ejar says whether it can be sent now
                                                            // (owner, 17 Oct 2026: a car in maintenance could not be moved from here)
                                                            const shown = statusLower.includes('rent') ? <RentedStatus plate={row.plate_number} car={row}>{cell}</RentedStatus>
                                                                : isInFleet(row) ? (
                                                                    <RequirePermission permission="fleet_ejar_transfer" fallback={cell}>
                                                                        <ReadyStatus plate={row.plate_number} onMoved={markMoved}>{cell}</ReadyStatus>
                                                                    </RequirePermission>
                                                                ) : cell;
                                                            const br = branchShort(getCarBranch(row.plate_number) || row.branch);
                                                            return br ? (
                                                                <span className="inline-flex items-center gap-1.5">
                                                                    {shown}
                                                                    <span className="text-[11px] font-semibold text-gray-500 tracking-wide" title={getCarBranch(row.plate_number) || row.branch}>{br}</span>
                                                                </span>
                                                            ) : shown;
                                                        })()}
                                                    </TableCell>
                                                    <TableCell>
                                                        {(() => {
                                                            const val = row.registration_expiry;
                                                            if (!val) return <CellText />;
                                                            // the same Dubai-day rule as the Expired card and tab (FLT-22)
                                                            const diffDays = daysToExpiry(val);
                                                            if (diffDays === null) return <span className="ds-cell-date">{val}</span>;

                                                            if (diffDays < 0) {
                                                                return (
                                                                    <span className="inline-flex items-center gap-1" title="Expired">
                                                                        <Icon glyph={ICONS.warning} size="xs" tone="danger" /><CellDate value={val} tone="danger" />
                                                                    </span>
                                                                );
                                                            }
                                                            if (diffDays <= 30) {
                                                                return (
                                                                    <span className="inline-flex items-center gap-1" title="Expiring within 30 days">
                                                                        <Icon glyph={ICONS.clock} size="xs" tone="warning" /><CellDate value={val} tone="warning" />
                                                                    </span>
                                                                );
                                                            }
                                                            return <span className="ds-cell-date">{val}</span>;
                                                        })()}
                                                    </TableCell>
                                                    <TableCell>
                                                        {(() => {
                                                            const status = getOilStatus(row.plate_number);
                                                            return (
                                                                <CellStatus
                                                                    status={status.label}
                                                                    tone={OIL_TONES[status.color] || 'neutral'}
                                                                    label={status.label}
                                                                />
                                                            );
                                                        })()}
                                                    </TableCell>
                                                    <TableCell><CellText>{row.color}</CellText></TableCell>
                                                    <TableCell><CellMoney value={row.rental_rate} /></TableCell>
                                                    <TableCell>
                                                        {/* Filled automatically from the car's rental (Monthly / Daily / Invygo / Leasing / Airport) */}
                                                        {(() => {
                                                            const usage = usageOf(row);
                                                            return usage
                                                                ? <CellTag tone={usage === 'Rented' ? 'neutral' : undefined}>{usage}</CellTag>
                                                                : <span className="ds-cell-empty">—</span>;
                                                        })()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {filteredCars.length > 0 && (
                                <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
                                    <span>{filteredCars.length} cars</span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={scrollToTop}
                                        leadingIcon={<Icon glyph={ICONS.chevronUp} size="sm" />}>
                                        Back to top
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {view === 'smart' && (
                        <SmartNavView
                            data={fleetCars}
                            selectedClass={selectedClass}
                            setSelectedClass={setSelectedClass}
                            selectedMake={selectedMake}
                            setSelectedMake={setSelectedMake}
                            getCarStatus={getCarStatus}
                        />
                    )}

                    {view === 'summary' && (
                        <SummaryView cars={fleetCars} getCarStatus={getCarStatus} usageOf={usageOf} rentalsLoading={!rentalsReady} />
                    )}
                </>
            )}

            <FilterSidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                options={filterOptions}
                matchCount={filteredCars.length}
                selected={selectedFilters}
                onToggle={toggleFilter}
                onClear={clearFilters}
                totalCars={cars.length}
                savedFilters={savedFilters}
                onSave={saveCurrentFilter}
                onLoad={loadSavedFilter}
                onDelete={deleteSavedFilter}
            />

            <LicenseModal key={licenseUrl || 'none'} url={licenseUrl} onClose={() => setLicenseUrl(null)} />

            <ExpiryModal
                isOpen={expiryModalOpen}
                onClose={() => setExpiryModalOpen(false)}
                expiryDate={expiryDateInput}
                setExpiryDate={setExpiryDateInput}
                onSearch={handleExpirySearch}
            />

            <EditVehicleModal
                isOpen={editVehicleModalOpen}
                onClose={() => setEditVehicleModalOpen(false)}
                car={editingCar}
                onSave={handleEditVehicleSave}
                isSaving={syncing}
            />
        </Layout>
    );
}

/** Toggle-chip classes for the filter drawer (selected state uses the accent). */
/** One spelling for the filters: capitals and accents ignored ("Citroën" = "CITROEN"). */
/** The text with the searched part marked (safe for any typed character, see highlightParts). */
function HighlightText({ text, highlight }) {
    return (
        <span>
            {highlightParts(text, highlight).map((part, i) => (
                part.match ? (
                    <mark key={i} className="bg-amber-100 text-gray-900 rounded-sm px-0.5">{part.text}</mark>
                ) : (
                    <span key={i}>{part.text}</span>
                )
            ))}
        </span>
    );
}

const foldText = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();

// The multi-select filters, in the order the panel shows them
const FACET_KEYS = ['statuses', 'categories', 'makes', 'models', 'years', 'colors'];

/** Ejar branch -> its short name beside the car status. */
function branchShort(branch) {
    const b = String(branch || '').toLowerCase();
    if (b.includes('invygo')) return 'INV';
    if (b.includes('terminal 1')) return 'T1';
    if (b.includes('terminal 2')) return 'T2';
    if (b.includes('business bay')) return 'B.B';
    return '';
}
// live statuses that mean "can be rented now"
const AVAILABLE_STATUSES = ['ready', 'available'];

/** "Rented": a click shows the car's open contract (its number opens the contract menu) and the customer's profile. */
const TRANSFER_POLL_MS = 1500;

/**
 * Status of any car not on rent (Ready, Maintenance ...) -> fast transfer. Ejar is asked where the car really is and
 * whether it offers "Send" (its own reason shown when not), then one
 * click sends it to another of our branches and receives it there: the engine does the send, both
 * E-Checks (full tank, black photos, the same KM) and the receive. A transfer that stopped half
 * way is offered to be finished instead - never sent twice.
 */
function ReadyStatus({ plate, children, onMoved }) {
    const [state, setState] = useState(undefined);       // undefined = asking Ejar, null = Ejar did not answer
    const [problem, setProblem] = useState('');
    const [job, setJob] = useState(null);                 // { status, steps[], to, error, result }
    const running = job && job.status === 'running';

    const load = async (open) => {
        if (!open || running) return;
        setJob(null);                                     // a finished transfer: reopening asks Ejar afresh
        setState(undefined); setProblem('');
        try { setState(await finesService.ejarVehicleState(plate)); } catch (e) { setState(null); setProblem(errorMessage(e)); }
    };

    const move = async (toBranchId, toShort, finishing) => {
        if (!(await confirmDialog({
            title: finishing ? `Finish the transfer of ${plate}?` : `Send ${plate} to ${toShort}?`,
            message: finishing
                ? 'The engine finishes it in Ejar: the missing E-Check and the receive.'
                : `The engine sends it from ${state.short} to ${toShort} in Ejar and receives it there - both E-Checks with a full tank, black photos and KM ${state.odometer ?? ''}.`,
            confirmLabel: finishing ? 'Finish' : `Send to ${toShort}`,
        }))) return;
        setJob({ status: 'running', steps: [], to: toShort });
        // the popup closes at the confirm: what happens is said here and beside the car's status (owner, 5 Oct 2026:
        // "مفيش حاجه بتقول انها شغالة")
        toast.info(`${plate}: ${finishing ? 'finishing the transfer' : `moving to ${toShort}`} in Ejar - about a minute`);
        try {
            const { jobId } = await finesService.ejarTransferStart(plate, toBranchId);
            // a failed poll is retried: "failed" here while Ejar is still moving the car would offer the transfer again
            const j = await followJob(() => finesService.ejarTransferJob(jobId), { intervalMs: TRANSFER_POLL_MS, onUpdate: setJob });
            if (j.status === 'done') {
                toast.success(`${plate} is at ${j.result?.short || toShort} in Ejar (transfer ${j.result?.number || ''})`);
                onMoved(plate, j.result || {});
            } else {
                toast.error(`${plate}: ${j.error || 'the transfer stopped'}`);
            }
        } catch (e) {
            setJob({ status: 'failed', steps: [], to: toShort, error: errorMessage(e) });
            toast.error(`${plate}: ${errorMessage(e)}`);
        }
    };

    const body = () => {
        if (job) {
            return (
                <div className="flex flex-col gap-1.5">
                    <div className="font-semibold text-gray-900">
                        {job.status === 'running' ? `Transferring to ${job.to}…` : job.status === 'done' ? `At ${job.result?.short || job.to} now` : 'Transfer stopped'}
                    </div>
                    {job.ejarUser && <div className="text-[11px] text-gray-500">On Ejar as <b className="font-mono">{job.ejarUser}</b></div>}
                    {(job.steps || []).map((s, i) => <div key={i} className="text-xs text-gray-700">✓ {s}</div>)}
                    {job.status === 'running' && <div className="text-xs text-gray-500">The engine is working in Ejar - you can close this.</div>}
                    {job.status === 'failed' && (
                        <>
                            <div className="text-xs text-red-700">{job.error}</div>
                            {job.stage && job.stage !== 'nothing' && <div className="text-[11px] text-gray-500">Open it again to finish the transfer.</div>}
                            <Button size="xs" onClick={() => { setJob(null); load(true); }}>Check again</Button>
                        </>
                    )}
                </div>
            );
        }
        if (state === undefined) return <span className="text-gray-500">Asking Ejar where {plate} is…</span>;
        if (state === null) return <span className="text-red-700">Ejar did not answer: {problem}</span>;
        const where = <div className="text-xs text-gray-600">In Ejar: <b>{state.short || '?'}</b>{state.odometer != null ? ` · KM ${Number(state.odometer).toLocaleString()}` : ''}</div>;
        if (state.stage === 'ready') {
            return (
                <div className="flex flex-col gap-2">
                    {where}
                    <div className="text-xs font-semibold text-gray-700">Send to</div>
                    <div className="flex gap-2 flex-wrap">
                        {(state.targets || []).map((t) => (
                            <Button key={t.id} size="xs" variant="primary" title={t.name} onClick={() => move(t.id, t.short, false)}>{t.short}</Button>
                        ))}
                    </div>
                </div>
            );
        }
        if (state.stage === 'send_check' || state.stage === 'receive') {
            const t = state.transfer || {};
            const to = t.toBranchId || (state.targets || [])[0]?.id;
            return (
                <div className="flex flex-col gap-2">
                    {where}
                    <div className="text-xs text-amber-800">
                        Transfer {t.number} stopped half way: {state.stage === 'send_check' ? 'its Send Car E-Check is missing' : `it waits to be received${t.to ? ` at ${t.to}` : ''}`}.
                    </div>
                    <Button size="xs" variant="primary" onClick={() => move(to, t.to || '', true)}>Finish the transfer</Button>
                </div>
            );
        }
        return <div className="flex flex-col gap-1">{where}<span className="text-xs text-gray-600">{state.reason || 'Ejar does not offer a transfer for this car now.'}</span></div>;
    };

    return (
        <Popover label={`Transfer ${plate}`} align="start" width={300} onOpenChange={load}
            trigger={({ props }) => (
                <button {...props} type="button" className="hover:underline cursor-pointer" title="Send this car to another branch (Ejar)">
                    {children}
                    {/* the transfer's own badge beside the status (owner, 17 Oct 2026: nothing showed it was working) */}
                    {running && (
                        <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-800">
                            <span className="h-3 w-3 animate-spin rounded-full border-2 border-purple-300 border-t-purple-800" aria-hidden />
                            Moving to {job.to}{(job.steps || []).length ? ` · step ${job.steps.length}` : ''}
                        </span>
                    )}
                    {job && job.status === 'done' && (
                        <span className="ml-1.5 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">✓ At {job.result?.short || job.to}</span>
                    )}
                    {job && job.status === 'failed' && (
                        <span className="ml-1.5 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">Transfer stopped</span>
                    )}
                </button>
            )}>
            {() => <div className="p-3 text-sm">{body()}</div>}
        </Popover>
    );
}

function RentedStatus({ plate, car, children }) {
    const [info, setInfo] = useState(undefined);        // undefined = not asked yet, null = none found
    const [profile, setProfile] = useState(null);
    const load = async (open) => {
        if (!open || (info !== undefined && info !== 'failed')) return;   // a failed read is asked again on the next open
        try {
            const found = await openContractFor(plate);
            // pick-up / drop-off as shown: date + time when the contract has one, never an invented 00:00 (owner, 27 Sep 2026)
            const rows = found ? await loadOpenContracts() : null;      // the same list, already read
            setInfo(found ? { ...found, pickText: contractDateText(rows, found, 'pick'), dropText: contractDateText(rows, found, 'drop') } : found);
        } catch (e) { setInfo('failed'); }
    };
    return (
        <>
            <Popover label={`Contract of ${plate}`} align="start" width={340} onOpenChange={load}
                trigger={({ props }) => (
                    <button {...props} type="button" className="hover:underline cursor-pointer" title="Who has this car? (contract and customer)">{children}</button>
                )}>
                {({ close }) => (
                    <div className="p-3 text-sm flex flex-col gap-2">
                        {info === undefined && <span className="text-gray-500">Looking for its contract…</span>}
                        {info === null && <span className="text-gray-500">No open contract found for {plate}.</span>}
                        {info === 'failed' && <span className="text-red-700">The contracts could not be read - close and open again to retry.</span>}
                        {info && info !== 'failed' && (
                            <>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-gray-500 shrink-0">Open contract</span>
                                    <span className="min-w-0 truncate"><CellId>{info.no}</CellId></span>
                                </div>
                                <div className="font-semibold text-gray-900">{info.customer || '—'}</div>
                                <div className="text-xs text-gray-600">
                                    {info.pickText || '—'} → {info.drop && info.drop < new Date() ? <span className="text-red-700 font-semibold">{info.dropText || '—'} (overdue)</span> : (info.dropText || '—')}
                                    {info.branch ? ` · ${info.branch}` : ''}
                                </div>
                                <div className="flex gap-2 pt-1">
                                    <Button size="xs" variant="primary" onClick={() => {
                                        close();
                                        setProfile({ name: info.customer, phone: info.phone, contractNo: info.no, plate, model: [car.make, car.model, car.year].filter(Boolean).join(' '), branch: info.branch, status: 'Open' });
                                    }}>Customer profile</Button>
                                </div>
                                <span className="text-[11px] text-gray-400">Click the contract number for Ejar, search and more.</span>
                            </>
                        )}
                    </div>
                )}
            </Popover>
            <CustomerProfileModal open={!!profile} seed={profile} onClose={() => setProfile(null)} onCopy={(m) => toast.info(m)} />
        </>
    );
}

const chipClass = (isSelected) => `px-3 py-1.5 rounded-md text-xs font-medium text-left border transition-colors ${isSelected
    ? 'bg-purple-50 border-purple-500 text-purple-700'
    : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`;

const FilterSection = ({ title, children }) => (
    <section>
        <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">{title}</h3>
        {children}
    </section>
);

/** One facet as chips: value + how many cars; nothing left -> a quiet line. */
const FacetChips = ({ type, list, selected, onToggle, wide = false, render }) => (
    list.length ? (
        <div className={wide ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap gap-2'}>
            {list.map(({ value, count }) => {
                const on = (selected[type] || []).includes(value);
                return (
                    <button type="button" key={value} aria-pressed={on} onClick={() => onToggle(type, value)}
                        className={`${chipClass(on)} ${count === 0 && !on ? 'opacity-50' : ''}`}>
                        <span className="flex items-center justify-between gap-2">
                            <span className="capitalize truncate">{render ? render(value) : value}</span>
                            <span className="tabular-nums text-gray-400">{count}</span>
                        </span>
                    </button>
                );
            })}
        </div>
    ) : <p className="text-xs text-gray-400">Nothing left with the other choices.</p>
);

const FilterSidebar = ({ isOpen, onClose, options, selected, onToggle, onClear, totalCars, matchCount, savedFilters, onSave, onLoad, onDelete }) => {
    const [newFilterName, setNewFilterName] = useState('');

    const activeCount = Object.values(selected).reduce((acc, curr) => {
        if (Array.isArray(curr)) return acc + curr.length;
        if (curr) return acc + 1;
        return acc;
    }, 0);
    const makesChosen = (selected.makes || []).length > 0;

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            title="Filters"
            description={`${activeCount} active · ${matchCount} car(s) match - each list shows only what your other choices leave`}
            size="sm"
            footer={(
                <>
                    <Button onClick={onClear} disabled={activeCount === 0}>
                        Clear All
                    </Button>
                    <Button onClick={onClose} variant="primary">
                        Show {matchCount} car(s)
                    </Button>
                </>
            )}
        >
            <div className="space-y-6">
                {options.totalCount < totalCars && (
                    <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        <Icon glyph={ICONS.warning} size="xs" />
                        Options for the {options.totalCount} car(s) of this view / search
                    </div>
                )}

                <FilterSection title="Car Status">
                    <FacetChips type="statuses" list={options.statuses} selected={selected} onToggle={onToggle} />
                </FilterSection>

                <FilterSection title="Category">
                    <FacetChips type="categories" list={options.categories} selected={selected} onToggle={onToggle} wide />
                </FilterSection>

                <FilterSection title="Manufacturer">
                    <FacetChips type="makes" list={options.makes} selected={selected} onToggle={onToggle} wide />
                </FilterSection>

                <FilterSection title="Model">
                    {makesChosen
                        ? <FacetChips type="models" list={options.models} selected={selected} onToggle={onToggle} wide />
                        : <p className="text-xs text-gray-400">Pick a manufacturer to see its models.</p>}
                </FilterSection>

                <FilterSection title="Year">
                    <FacetChips type="years" list={options.years} selected={selected} onToggle={onToggle} />
                </FilterSection>

                <FilterSection title="Color">
                    <FacetChips type="colors" list={options.colors} selected={selected} onToggle={onToggle} wide
                        render={(color) => (
                            <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full border border-gray-200" style={{ backgroundColor: String(color).toLowerCase() }} aria-hidden="true"></span>
                                {color}
                            </span>
                        )} />
                </FilterSection>

                {/* KM Range Filter */}
                <FilterSection title="Mileage (KM)">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Min KM">
                            <Input size="sm" type="number" placeholder="0" value={selected.kmMin}
                                onChange={(e) => onToggle('kmMin', e.target.value, true)} />
                        </Field>
                        <Field label="Max KM">
                            <Input size="sm" type="number" placeholder="200,000" value={selected.kmMax}
                                onChange={(e) => onToggle('kmMax', e.target.value, true)} />
                        </Field>
                    </div>
                </FilterSection>

                {/* Expiry Date Ranges */}
                <FilterSection title="Registration Expiry">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="From">
                            <Input size="sm" type="date" value={selected.regDateStart} onChange={(e) => onToggle('regDateStart', e.target.value, true)} />
                        </Field>
                        <Field label="To">
                            <Input size="sm" type="date" value={selected.regDateEnd} onChange={(e) => onToggle('regDateEnd', e.target.value, true)} />
                        </Field>
                    </div>
                </FilterSection>

                <FilterSection title="Insurance Expiry">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="From">
                            <Input size="sm" type="date" value={selected.insDateStart} onChange={(e) => onToggle('insDateStart', e.target.value, true)} />
                        </Field>
                        <Field label="To">
                            <Input size="sm" type="date" value={selected.insDateEnd} onChange={(e) => onToggle('insDateEnd', e.target.value, true)} />
                        </Field>
                    </div>
                </FilterSection>

                {/* Saved Presets */}
                {savedFilters.length > 0 && (
                    <FilterSection title="Saved Presets">
                        <div className="grid grid-cols-1 gap-2">
                            {savedFilters.map(preset => (
                                <div key={preset.id} className="flex items-center gap-1">
                                    <button type="button" onClick={() => onLoad(preset.filters)} className={`flex-1 ${chipClass(false)}`}>
                                        {preset.name}
                                    </button>
                                    <IconButton icon={ICONS.close} label={`Delete preset ${preset.name}`} size="xs" onClick={() => onDelete(preset.id)} />
                                </div>
                            ))}
                        </div>
                    </FilterSection>
                )}

                <FilterSection title="Save Current Filters">
                    <div className="flex gap-2">
                        <Input size="sm" aria-label="Preset name" placeholder="Filter Name..." value={newFilterName}
                            onChange={(e) => setNewFilterName(e.target.value)} className="flex-1" />
                        <Button size="sm" onClick={() => { onSave(newFilterName); setNewFilterName(''); }} disabled={!newFilterName.trim()}>
                            Save
                        </Button>
                    </div>
                </FilterSection>
            </div>
        </Drawer>
    );
};

const FilterChips = ({ selected, onToggle, onClear }) => {
    const activeFilters = [];
    Object.entries(selected).forEach(([type, values]) => {
        if (Array.isArray(values)) {
            values.forEach(val => {
                activeFilters.push({ type, val, isArray: true });
            });
        } else if (values && values !== '') {
            let label = values;
            if (type.includes('Date')) label = new Date(values).toLocaleDateString('en-GB');
            activeFilters.push({ type, val: values, label, isArray: false });
        }
    });

    if (activeFilters.length === 0) return null;

    const getFilterLabel = (type) => {
        const labels = {
            categories: 'Category',
            makes: 'Make',
            models: 'Model',
            statuses: 'Status',
            years: 'Year',
            colors: 'Color',
            regDateStart: 'Reg From',
            regDateEnd: 'Reg To',
            insDateStart: 'Ins From',
            insDateEnd: 'Ins To',
            kmMin: 'Min KM',
            kmMax: 'Max KM'
        };
        return labels[type] || type;
    };

    return (
        <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs font-medium text-gray-600 mr-1">Filters:</span>
            {activeFilters.map(({ type, val, label, isArray }) => (
                <span key={`${type}-${val}`} className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-800 pl-2 pr-1 py-0.5 rounded-md text-xs">
                    <span className="text-gray-500">{getFilterLabel(type)}:</span>
                    <span className="font-medium">{label || val}</span>
                    <IconButton
                        icon={ICONS.close}
                        label={`Remove ${getFilterLabel(type)} ${label || val}`}
                        size="xs"
                        onClick={() => onToggle(type, isArray ? val : '', !isArray)}
                    />
                </span>
            ))}
            <Button variant="link" size="xs" onClick={onClear}>
                Clear all filters
            </Button>
        </div>
    );
};

const SummaryCard = ({ label, value, tone, icon, active, onClick, meta }) => (
    <Stat
        label={label}
        value={value}
        tone={tone}
        icon={icon}
        onClick={onClick}
        meta={active ? 'Showing' : meta}
        className={active ? 'border-purple-500 ring-1 ring-purple-500' : ''}
    />
);

const EditVehicleModal = ({ isOpen, onClose, car, onSave, isSaving }) => {
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (car && isOpen) {
            setFormData({
                make: car.make || '',
                model: car.model || '',
                year: car.year || '',
                category: car.category || '',
                color: car.color || '',
                vin: car.vin || '',
                rental_rate: car.rental_rate || '',
                registration_expiry: car.registration_expiry || '',
                insurance_expiry: car.insurance_expiry || '',
                status: car.status || 'active',
                remarks: car.remarks || ''
            });
        }
    }, [car, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(car.plate_number, formData);
    };

    return (
        <Dialog
            open={Boolean(isOpen && car)}
            onClose={onClose}
            title="Edit Vehicle Data"
            description={car ? `Plate Number: ${car.plate_number}` : undefined}
            size="lg"
            closeOnOverlay={false}
            footer={(
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button form="edit-vehicle-form" type="submit" variant="primary" isLoading={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </>
            )}
        >
            {car && (
                <form id="edit-vehicle-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Manufacturer">
                        <Input name="make" value={formData.make ?? ''} onChange={handleChange} />
                    </Field>
                    <Field label="Model">
                        <Input name="model" value={formData.model ?? ''} onChange={handleChange} />
                    </Field>
                    <Field label="Year">
                        <Input name="year" value={formData.year ?? ''} onChange={handleChange} type="number" />
                    </Field>
                    <Field label="Class/Category">
                        <Input name="category" value={formData.category ?? ''} onChange={handleChange} />
                    </Field>
                    <Field label="Color">
                        <Input name="color" value={formData.color ?? ''} onChange={handleChange} />
                    </Field>
                    <Field label="Chassis No. (VIN)">
                        <Input name="vin" value={formData.vin ?? ''} onChange={handleChange} className="font-mono" />
                    </Field>
                    <Field label="Rental Rate">
                        <Input name="rental_rate" value={formData.rental_rate ?? ''} onChange={handleChange} type="number" className="font-mono" />
                    </Field>
                    <Field label="Vehicle Status">
                        <Select name="status" value={formData.status ?? 'active'} onChange={handleChange}>
                            <option value="active">Active (Yelo)</option>
                            <option value="maintenance">Maintenance</option>
                            <option value="sold">Sold</option>
                            <option value="inactive">Inactive</option>
                        </Select>
                    </Field>
                    <Field label="Reg Expiry">
                        <Input name="registration_expiry" value={formData.registration_expiry ?? ''} onChange={handleChange} placeholder="DD/MM/YYYY" className="font-mono" />
                    </Field>
                    <Field label="Ins Expiry">
                        <Input name="insurance_expiry" value={formData.insurance_expiry ?? ''} onChange={handleChange} placeholder="DD/MM/YYYY" className="font-mono" />
                    </Field>
                    <Field label="Remarks / Company" className="md:col-span-2">
                        <Input name="remarks" value={formData.remarks ?? ''} onChange={handleChange} />
                    </Field>
                </form>
            )}
        </Dialog>
    );
};
